import React, { useCallback, useContext, useEffect, useState } from "react";
import {
  IonButton,
  IonButtons,
  IonContent,
  IonFooter,
  IonHeader,
  IonInput,
  IonItem,
  IonLabel,
  IonModal,
  IonRadio,
  IonRadioGroup,
  IonTitle,
  IonToolbar,
} from "@ionic/react";
import { TranslationsContext } from "../util/Translations";
import { Employee } from "../models/employees/Employees";
import BarcodeScannerButton from "./BarcodeScannerButton";

interface EmployeeSearchProps {
  isOpen: boolean;
  employees: Employee[];
  onSelect: (employee: Employee) => void;
  onCancel: () => void;
  title?: string;
}

const EmployeeSearch: React.FC<EmployeeSearchProps> = (props) => {
  const { isOpen, employees, onSelect, onCancel, title } = props;
  const { translations } = useContext(TranslationsContext);
  const [filterString, setFilterString] = useState<string>();
  const [filteredItems, setFilteredItems] = useState<Employee[]>(employees);
  const [selectedEmployeeId, setSelectedEmployeeId] = useState("");

  useEffect(() => {
    if (!filterString) {
      setFilteredItems(employees);
    } else {
      const newArray = employees.filter((employee) => {
        return (
          employee.EmployeeId.includes(filterString) ||
          employee.Properties?.EmployeeName?.includes(filterString)
        );
      });
      setFilteredItems(newArray);
    }
  }, [filterString]);

  useEffect(() => {
    setFilteredItems(employees);
  }, [employees]);

  const onScan = (text?: string) => {
    text && setFilterString(text);
  };

  const handleSelect = useCallback(() => {
    if (selectedEmployeeId) {
      const emp = filteredItems.find(
        (i) => i.EmployeeId === selectedEmployeeId
      );
      if (emp) onSelect(emp);
    }
    onCancel();
  }, [filteredItems, onCancel, onSelect, selectedEmployeeId]);

  return (
    <IonModal isOpen={isOpen} onDidDismiss={onCancel}>
      <IonHeader>
        <IonToolbar>
          <IonButtons slot="start">
            <IonButton color="primary" onClick={onCancel}>
              {translations["lbl_btn_cancel"] || "Cancel"}
            </IonButton>
          </IonButtons>
          <IonTitle>{title}</IonTitle>
          <IonButtons slot="end">
            <BarcodeScannerButton onScan={onScan} />
          </IonButtons>
        </IonToolbar>
      </IonHeader>
      <IonContent class="ion-padding">
        <IonItem>
          <IonInput
            value={filterString}
            onIonChange={(ev) => {
              setFilterString(ev.detail.value ?? undefined);
            }}
            debounce={250}
            placeholder={translations["lbl_btn_search"] || "Search"}
          ></IonInput>
        </IonItem>
        <IonRadioGroup
          onIonChange={(e) => setSelectedEmployeeId(e.target.value ?? "")}
          value={selectedEmployeeId}
        >
          {filteredItems.map((employee) => (
            <IonItem key={employee.EmployeeId}>
              <IonLabel>
                {`${employee.EmployeeId}`}
                {employee.Properties?.EmployeeName &&
                  ` - ${employee.Properties?.EmployeeName}`}
              </IonLabel>
              <IonRadio slot="start" value={employee.EmployeeId} />
            </IonItem>
          ))}
        </IonRadioGroup>
      </IonContent>
      <IonFooter>
        <IonButton expand="block" onClick={handleSelect}>
          OK
        </IonButton>
        <IonButton fill="outline" expand="block" onClick={onCancel}>
          Cancel
        </IonButton>
      </IonFooter>
    </IonModal>
  );
};

export default EmployeeSearch;
